import { IPromise } from 'angular';
import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { PublishedConnectionMapper } from '../mappers/PublishedConnectionMapper';
import { PublishedConnectionDefinition } from '../models/ContentDefinitions';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { Module } from '../Module';
import { AddTagsToPublishedConnectionsRequest } from '../ServerApi/requests/AddTagsToPublishedConnectionsRequest';
import { DeletePublishedConnectionsRequest } from '../ServerApi/requests/DeletePublishedConnectionsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetPublishedConnectionActionsRequest } from '../ServerApi/requests/GetPublishedConnectionActionsRequest';
import { GetPublishedConnectionsRequest } from '../ServerApi/requests/GetPublishedConnectionsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../ServerApi/requests/MarkRecentlyViewedRequest';
import { MovePublishedConnectionsToProject } from '../ServerApi/requests/MovePublishedConnectionsToProjectRequest';
import { RemoveTagsFromPublishedConnectionsRequest } from '../ServerApi/requests/RemoveTagsFromPublishedConnectionsRequest';
import { IPublishedConnectionDescriptionParams, SetPublishedConnectionDescriptionRequest } from '../ServerApi/requests/SetPublishedConnectionDescriptionRequest';
import { SetPublishedConnectionNameRequest } from '../ServerApi/requests/SetPublishedConnectionNameRequest';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IPublishedConnectionActions } from '../ServerApi/types/IPublishedConnectionActions';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class PublishedConnections implements IListResource<IPublishedConnectionInfo>, IFieldValuesResource, IDetailResource<IPublishedConnectionInfo>, IActionResource<IPublishedConnectionActions> {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'ToasterService',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IPublishedConnectionInfo>;
  private allQuery: ResourceQuery<IPublishedConnectionInfo>;
  private detailFetcher: IDetailFetcher<IPublishedConnectionInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    private ToasterService: ToasterService,
    FetcherFactory: FetcherFactory
  ) {
    if (FeatureFlags.publishedConnectionContentManagementSP1Enabled) {
      this.server = ErrorNotifyingServer.getServerService();
      this.listFetcher = FetcherFactory.getServerResourceFetcher(GetPublishedConnectionsRequest, ServerApiMapper.publishedConnections);
      this.allQuery = new ResourceQuery(this.listFetcher);
      this.detailFetcher = FetcherFactory.getServerDetailFetcher('getPublishedConnection', PublishedConnectionMapper.mapDetail);
      this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    }
  }

  public all(): ResourceQuery<IPublishedConnectionInfo> {
    return this.allQuery;
  }

  public getById(id: string): ng.IPromise<IPublishedConnectionInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'publishedConnection');
  }

  // Actions
  public actions(id: string): ng.IPromise<IPublishedConnectionActions> {
    return this.server.sendRequest(new GetPublishedConnectionActionsRequest(id));
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new SetPublishedConnectionNameRequest({ id: id, name: name }));
  }

  public del(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeletePublishedConnectionsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, publishedConnectionIds: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(publishedConnectionIds,
        (idsBlock) => new MovePublishedConnectionsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToPublishedConnectionsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromPublishedConnectionsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: IPublishedConnectionDescriptionParams): Promise<IResult> => {
    return this.ErrorNotifyingServer
      .sendRequestAndDelay(new SetPublishedConnectionDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  }

  public markRecentlyViewed(id: string): IPromise<IResult> {
    return this.server.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.PublishedConnection
    }));
  }

  public static isPublishedConnectionInfo(item: any): item is IPublishedConnectionInfo {
    return item &&
           item.contentType &&
           item.contentType === PublishedConnectionDefinition.contentType;
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      this.ToasterService.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      this.ToasterService.pop({
        type: ToastType.Info,
        text: successMessage
      });
    }
  }
}

Module.instance().service('PublishedConnections', PublishedConnections);
